import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  Info as InfoIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { calculateBSA, calculateDose } from '../../services/oncologyService';
import { BSA_FORMULAS, BSA_FORMULA_DESCRIPTIONS } from '../../types/enums';

const DoseCalculator = ({ patientData, onDoseCalculated }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // Patient measurements
  const [heightCm, setHeightCm] = useState(patientData?.heightCm || '');
  const [weightKg, setWeightKg] = useState(patientData?.weightKg || '');
  const [bsaFormula, setBsaFormula] = useState('Mosteller');

  // BSA Result
  const [bsaResult, setBsaResult] = useState(null);

  // Dose calculation
  const [standardDose, setStandardDose] = useState('');
  const [doseUnit, setDoseUnit] = useState('mg/m²');
  const [maxDose, setMaxDose] = useState('');
  const [reductionPercent, setReductionPercent] = useState('');
  const [doseResult, setDoseResult] = useState(null);

  // History of calculations
  const [calculationHistory, setCalculationHistory] = useState([]);

  useEffect(() => {
    if (patientData?.heightCm) setHeightCm(patientData.heightCm);
    if (patientData?.weightKg) setWeightKg(patientData.weightKg);
  }, [patientData]);

  const handleCalculateBSA = async () => {
    if (!heightCm || !weightKg) {
      setError('Please enter height and weight');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await calculateBSA(
        parseFloat(heightCm),
        parseFloat(weightKg),
        bsaFormula
      );

      setBsaResult(result);
    } catch (err) {
      console.error('Error calculating BSA:', err);
      setError(err.message || 'Failed to calculate BSA');
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateDose = async () => {
    if (!bsaResult?.bsaValue || !standardDose) {
      setError('Please calculate BSA first and enter standard dose');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await calculateDose({
        bsaValue: bsaResult.bsaValue,
        standardDose: parseFloat(standardDose),
        doseUnit,
        weightKg: parseFloat(weightKg),
        maxDose: maxDose ? parseFloat(maxDose) : undefined,
        reductionPercent: reductionPercent ? parseFloat(reductionPercent) : undefined
      });

      setDoseResult(result);

      // Add to history
      setCalculationHistory(prev => [
        {
          timestamp: new Date().toISOString(),
          bsa: bsaResult.bsaValue,
          standardDose,
          doseUnit,
          calculatedDose: result.calculatedDose,
          reduction: reductionPercent || 0,
          capped: result.cappedAtMax
        },
        ...prev.slice(0, 9) // Keep last 10
      ]);

      if (onDoseCalculated) {
        onDoseCalculated(result);
      }
    } catch (err) {
      console.error('Error calculating dose:', err);
      setError(err.message || 'Failed to calculate dose');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyDose = () => {
    if (doseResult) {
      navigator.clipboard.writeText(`${doseResult.calculatedDose} ${doseResult.doseUnit}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const doseUnits = ['mg/m²', 'mg/kg', 'mg', 'mg/m²/day', 'AUC'];

  const reductionLevels = [
    { value: '', label: 'No Reduction (100%)' },
    { value: '25', label: 'Level 1 (75%)' },
    { value: '50', label: 'Level 2 (50%)' },
    { value: '75', label: 'Level 3 (25%)' }
  ];

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Chemotherapy Dose Calculator
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Calculate body surface area (BSA) and chemotherapy doses with dose reduction support.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* BSA Calculation Section */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Step 1: Calculate BSA
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Height (cm)"
                    type="number"
                    value={heightCm}
                    onChange={(e) => setHeightCm(e.target.value)}
                    inputProps={{ min: 0, step: 0.1 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Weight (kg)"
                    type="number"
                    value={weightKg}
                    onChange={(e) => setWeightKg(e.target.value)}
                    inputProps={{ min: 0, step: 0.1 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>BSA Formula</InputLabel>
                    <Select
                      value={bsaFormula}
                      label="BSA Formula"
                      onChange={(e) => setBsaFormula(e.target.value)}
                    >
                      {BSA_FORMULAS.map(formula => (
                        <MenuItem key={formula} value={formula}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {formula}
                            <Tooltip title={BSA_FORMULA_DESCRIPTIONS[formula]}>
                              <InfoIcon fontSize="small" color="action" />
                            </Tooltip>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleCalculateBSA}
                    disabled={loading || !heightCm || !weightKg}
                    startIcon={loading ? <CircularProgress size={20} /> : <CalculateIcon />}
                  >
                    Calculate BSA
                  </Button>
                </Grid>
              </Grid>

              {bsaResult && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                  <Typography variant="h4" color="primary.contrastText" align="center">
                    {bsaResult.bsaValue} m²
                  </Typography>
                  <Typography variant="body2" color="primary.contrastText" align="center">
                    Body Surface Area ({bsaResult.formula} formula)
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Dose Calculation Section */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Step 2: Calculate Dose
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    label="Standard Dose"
                    type="number"
                    value={standardDose}
                    onChange={(e) => setStandardDose(e.target.value)}
                    inputProps={{ min: 0, step: 0.1 }}
                    placeholder="Enter protocol dose"
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <InputLabel>Unit</InputLabel>
                    <Select
                      value={doseUnit}
                      label="Unit"
                      onChange={(e) => setDoseUnit(e.target.value)}
                    >
                      {doseUnits.map(unit => (
                        <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Max Dose (optional)"
                    type="number"
                    value={maxDose}
                    onChange={(e) => setMaxDose(e.target.value)}
                    inputProps={{ min: 0, step: 0.1 }}
                    placeholder="Dose cap"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Dose Reduction</InputLabel>
                    <Select
                      value={reductionPercent}
                      label="Dose Reduction"
                      onChange={(e) => setReductionPercent(e.target.value)}
                    >
                      {reductionLevels.map(level => (
                        <MenuItem key={level.value} value={level.value}>
                          {level.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    onClick={handleCalculateDose}
                    disabled={loading || !bsaResult || !standardDose}
                    startIcon={loading ? <CircularProgress size={20} /> : <CalculateIcon />}
                  >
                    Calculate Dose
                  </Button>
                </Grid>
              </Grid>

              {doseResult && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'secondary.light', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h4" color="secondary.contrastText">
                        {doseResult.calculatedDose} {doseResult.doseUnit}
                      </Typography>
                      <Typography variant="body2" color="secondary.contrastText">
                        Calculated Dose
                        {reductionPercent && ` (${reductionPercent}% reduction applied)`}
                      </Typography>
                    </Box>
                    <Tooltip title={copied ? 'Copied!' : 'Copy dose'}>
                      <IconButton onClick={handleCopyDose} sx={{ color: 'secondary.contrastText' }}>
                        {copied ? <CheckIcon /> : <CopyIcon />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {doseResult.cappedAtMax && (
                    <Chip
                      label="Capped at maximum dose"
                      color="warning"
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Calculation History */}
        {calculationHistory.length > 0 && (
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Calculation History
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>BSA (m²)</TableCell>
                    <TableCell>Standard Dose</TableCell>
                    <TableCell>Reduction</TableCell>
                    <TableCell>Calculated Dose</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {calculationHistory.map((calc, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {new Date(calc.timestamp).toLocaleTimeString()}
                      </TableCell>
                      <TableCell>{calc.bsa}</TableCell>
                      <TableCell>{calc.standardDose} {calc.doseUnit}</TableCell>
                      <TableCell>{calc.reduction}%</TableCell>
                      <TableCell fontWeight="bold">
                        {calc.calculatedDose} {calc.doseUnit.replace('/m²', '').replace('/kg', '')}
                      </TableCell>
                      <TableCell>
                        {calc.capped && <Chip label="Capped" size="small" color="warning" />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}
      </Grid>

      {/* Formula Reference */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          BSA Formula Reference
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>Mosteller:</strong> BSA = √((height × weight) / 3600) — Most commonly used<br />
          <strong>DuBois:</strong> BSA = 0.007184 × height^0.725 × weight^0.425<br />
          <strong>Haycock:</strong> BSA = 0.024265 × height^0.3964 × weight^0.5378 — Recommended for children<br />
        </Typography>
      </Box>
    </Paper>
  );
};

export default DoseCalculator;

